import { useState, useEffect, useCallback } from 'react';
import api, { formatApiError } from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import HelpTooltip from '@/components/help/HelpTooltip';
import { Mail, AlertTriangle, CheckCircle2, ExternalLink } from 'lucide-react';

/**
 * Amazon SES — infraestructura de correo compartida.
 *
 * Una sola cuenta de AWS (la de Cavernícola) manda por todos los tenants, y
 * cada tenant verifica SU dominio dentro de ella. SES no tiene subcuentas: lo
 * que hay son «identidades verificadas», y los 3 CNAME que se le piden al
 * cliente son exactamente eso.
 *
 * De ahí sale lo que se ve en esta pantalla:
 *
 *  - El aviso de MODO PRUEBA. Toda cuenta nueva de SES nace limitada a 200
 *    correos al día y solo a direcciones verificadas una por una. Es la
 *    primera piedra con la que se tropieza todo el mundo, así que se dice
 *    fuerte en cuanto se detecta, en vez de dejar que el dueño lo descubra
 *    cuando una campaña no salga.
 *  - El corte automático. Como la cuenta es una sola, la reputación es una
 *    sola: si un tenant rebasa los límites de AWS, AWS apaga la cuenta entera
 *    y dejan de enviar todos. Esta casilla es lo que corta al que se pasa
 *    antes de que AWS corte a todos, y por eso viene encendida.
 */
export default function SesInfraSection() {
  const [datos, setDatos] = useState(null);
  const [form, setForm] = useState({
    access_key_id: '', secret_access_key: '', region: 'us-east-1',
    cupo_por_defecto: 500, corte_automatico: true,
  });
  const [guardando, setGuardando] = useState(false);
  const [probando, setProbando] = useState(false);
  const [prueba, setPrueba] = useState(null);

  const cargar = useCallback(async () => {
    try {
      const { data } = await api.get('/admin/infrastructure/ses');
      setDatos(data);
      setForm(f => ({
        ...f,
        region: data.region || 'us-east-1',
        cupo_por_defecto: data.cupo_por_defecto ?? 500,
        corte_automatico: data.corte_automatico !== false,
      }));
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  const guardar = async () => {
    setGuardando(true);
    try {
      await api.put('/admin/infrastructure/ses', form);
      toast.success('Configuración de Amazon SES guardada');
      setForm(f => ({ ...f, secret_access_key: '' }));   // no se conserva en pantalla
      await cargar();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setGuardando(false);
    }
  };

  const probar = async () => {
    setProbando(true);
    setPrueba(null);
    try {
      const { data } = await api.post('/admin/infrastructure/ses/probar');
      setPrueba(data);
      if (data.ok) toast.success('AWS respondió correctamente');
      else toast.error('AWS no aceptó la conexión');
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setProbando(false);
    }
  };

  if (!datos) return null;

  return (
    <div className="border-2 rounded-xl p-5 border-[#FF9900]/30 bg-[#FFF8F0]"
         data-testid="infra-amazon-ses">
      <div className="h-1.5 -mt-5 -mx-5 mb-4 rounded-t-xl bg-[#FF9900]" />

      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Mail className="w-4 h-4 text-[#B45309]" />
          <h2 className="text-base font-bold text-[#B45309]">Correo masivo (Amazon SES)</h2>
          <span className={`text-[10px] px-2 py-0.5 rounded-full ${
            datos.configurado ? 'bg-emerald-50 text-emerald-600' : 'bg-white/80 text-[#94A3B8]'
          }`}>
            {datos.configurado ? 'Configurado' : 'Sin configurar'}
          </span>
        </div>
        <a href="https://console.aws.amazon.com/ses/home#/account" target="_blank" rel="noreferrer"
           className="text-xs text-[#B45309] hover:underline font-medium">
          Abrir Amazon SES <ExternalLink className="w-3 h-3 inline" />
        </a>
      </div>

      <p className="text-sm text-[#78350F] mb-4 leading-relaxed">
        Tu cuenta de AWS manda el correo de todos los clientes, y cada uno verifica su
        propio dominio. El cliente ve salir el correo a su nombre —ni Cavernícola ni
        Amazon aparecen— y tú controlas cuánto puede enviar cada quien.
      </p>

      <div className="grid gap-3 sm:grid-cols-2">
        <div>
          <Label className="text-xs flex items-center gap-1">
            Access Key ID
            <HelpTooltip text="La primera de las dos llaves que genera AWS. Empieza con AKIA. Se crea en IAM → Usuarios → tu usuario → Credenciales de seguridad → Crear clave de acceso." />
          </Label>
          <Input value={form.access_key_id} className="bg-white text-sm font-mono"
            placeholder={datos.access_key_id_visible || 'AKIA...'}
            onChange={e => setForm(f => ({ ...f, access_key_id: e.target.value }))}
            data-testid="ses-access-key" />
        </div>
        <div>
          <Label className="text-xs flex items-center gap-1">
            Secret Access Key
            <HelpTooltip text="La segunda llave. AWS solo te la enseña UNA vez, al crearla: si no la copiaste, hay que generar una nueva. Aquí se guarda cifrada y ya no se vuelve a mostrar." />
          </Label>
          <Input type="password" value={form.secret_access_key} className="bg-white text-sm font-mono"
            placeholder={datos.configurado ? '•••••••• (guardada, déjalo vacío para conservarla)' : ''}
            onChange={e => setForm(f => ({ ...f, secret_access_key: e.target.value }))}
            data-testid="ses-secret-key" />
        </div>
        <div>
          <Label className="text-xs flex items-center gap-1">
            Región de AWS
            <HelpTooltip text="Es la ciudad donde vive tu cuenta de SES. Sale arriba a la derecha en la consola de AWS. Tiene que ser la MISMA donde verificaste tu cuenta; si eliges otra, AWS dirá que no encuentra nada." />
          </Label>
          <Select value={form.region} onValueChange={v => setForm(f => ({ ...f, region: v }))}>
            <SelectTrigger className="bg-white text-sm" data-testid="ses-region"><SelectValue /></SelectTrigger>
            <SelectContent>
              {(datos.regiones || []).map(r => (
                <SelectItem key={r.valor} value={r.valor}>{r.etiqueta}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs flex items-center gap-1">
            Envíos al mes por cliente
            <HelpTooltip text="Cuántos correos puede mandar cada cliente al mes con tu infraestructura. Se reinicia cada mes. A un cliente en particular se le puede subir desde su ficha. Si el cliente conecta su propio proveedor de correo, este límite deja de aplicarle." />
          </Label>
          <Input type="number" min="0" value={form.cupo_por_defecto} className="bg-white text-sm"
            onChange={e => setForm(f => ({ ...f, cupo_por_defecto: Number(e.target.value) }))}
            data-testid="ses-cupo" />
        </div>
      </div>

      <div className="flex items-start gap-3 mt-4 p-3 rounded-lg bg-white border border-[#FDE68A]">
        <Switch checked={form.corte_automatico} data-testid="ses-corte-automatico"
          onCheckedChange={v => setForm(f => ({ ...f, corte_automatico: v }))} />
        <div className="min-w-0">
          <p className="text-sm font-semibold text-[#0F172A]">
            Cortar solo al cliente que ponga en riesgo la cuenta
          </p>
          <p className="text-xs text-[#64748B] leading-relaxed mt-0.5">
            Todos tus clientes envían desde la misma cuenta de AWS, así que la reputación
            es una sola. Si uno manda a una lista vieja o comprada y la gente lo marca como
            spam, <strong>Amazon no lo castiga a él: apaga tu cuenta y dejan de enviar todos</strong>.
            Con esto encendido, CavernicolApp le corta el envío a ese cliente —solo a él— y
            te avisa. Amazon revisa a partir de {datos.limites_aws?.rebotes_pct ?? 5}% de
            correos rebotados o {datos.limites_aws?.quejas_pct ?? 0.1}% de quejas.
          </p>
        </div>
      </div>

      <div className="flex gap-2 mt-4">
        <Button size="sm" onClick={guardar} disabled={guardando}
          className="bg-[#B45309] hover:bg-[#92400E]" data-testid="ses-guardar">
          {guardando ? 'Guardando…' : 'Guardar'}
        </Button>
        <Button size="sm" variant="outline" onClick={probar}
          disabled={probando || !datos.configurado} data-testid="ses-probar">
          {probando ? 'Consultando a AWS…' : 'Probar conexión'}
        </Button>
      </div>

      {prueba && (
        <div className={`mt-4 rounded-lg border p-3 ${
          prueba.ok ? 'bg-white border-[#E2E8F0]' : 'bg-red-50 border-red-200'
        }`} data-testid="ses-resultado-prueba">
          <p className="text-sm font-semibold flex items-center gap-1.5 text-[#0F172A]">
            {prueba.ok ? <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                       : <AlertTriangle className="w-4 h-4 text-red-600" />}
            {prueba.ok ? prueba.resumen : 'No se pudo conectar'}
          </p>
          {!prueba.ok && <p className="text-sm text-red-700 mt-1">{prueba.motivo}</p>}

          {prueba.ok && (
            <div className="flex flex-wrap gap-x-5 gap-y-1 mt-2 text-xs text-[#475569]">
              <span>Región: <strong>{prueba.region}</strong></span>
              <span>Tope diario de AWS: <strong>{prueba.limite_diario?.toLocaleString()}</strong></span>
              <span>Enviados hoy: <strong>{prueba.enviados_ultimas_24h?.toLocaleString()}</strong></span>
            </div>
          )}

          {(prueba.avisos || []).map((aviso, i) => (
            <div key={i} className="mt-3 p-3 rounded-md bg-amber-50 border border-amber-200
                                    text-sm text-amber-900 leading-relaxed">
              <AlertTriangle className="w-4 h-4 inline mr-1 -mt-0.5" />
              {aviso}
            </div>
          ))}

          {prueba.modo_prueba && (
            <a href="https://console.aws.amazon.com/ses/home#/account" target="_blank" rel="noreferrer"
               className="inline-block mt-2 text-xs font-medium text-[#B45309] hover:underline">
              Ir a pedir el acceso de producción <ExternalLink className="w-3 h-3 inline" />
            </a>
          )}
        </div>
      )}

      <details className="mt-4 group">
        <summary className="text-xs font-medium text-[#B45309] cursor-pointer hover:underline">
          ¿De dónde saco estas llaves? (paso a paso)
        </summary>
        <ol className="mt-2 space-y-1.5 text-xs text-[#475569] list-decimal list-inside leading-relaxed">
          <li>Entra a la consola de AWS y busca <strong>IAM</strong> arriba.</li>
          <li>Menú izquierdo → <strong>Usuarios</strong> → <strong>Crear usuario</strong>. Ponle un nombre
              como <code className="bg-white px-1 rounded">cavernicolapp-correo</code>.</li>
          <li>Cuando pregunte si necesita entrar a la consola, di que <strong>no</strong>: esta cuenta es
              solo para que el sistema mande correo, no para que una persona entre.</li>
          <li>En permisos, elige <strong>Adjuntar políticas directamente</strong> y busca
              <code className="bg-white px-1 rounded mx-1">AmazonSESFullAccess</code>.</li>
          <li>Ya creado el usuario, entra a él → pestaña <strong>Credenciales de seguridad</strong> →
              <strong>Crear clave de acceso</strong> → elige <em>Aplicación fuera de AWS</em>.</li>
          <li>Copia las <strong>dos</strong> llaves y pégalas aquí arriba. La segunda AWS solo la muestra
              esa vez: si cierras la ventana sin copiarla, hay que generar otra.</li>
        </ol>
      </details>
    </div>
  );
}
